/**
 * Shared KPI calculation engine.
 *
 * Ported from dna-fpm-frontend/src/engine/kpi_logic.py.
 * Handles both forward derivation (irreducible → derived) and reverse
 * derivation (editing a derived metric → updating irreducible metrics).
 *
 * Operates on scalar MetricValues (Record<string, number>), not DataFrames.
 * Used by both the summary page and (eventually) the temporal page.
 */

export type MetricValues = Record<string, number>;

// ---------------------------------------------------------------------------
// Metric name constants
// ---------------------------------------------------------------------------

const GIS1 = "GIS1";
const GIR = "GIR";
const NIS3 = "NIS3";
const GS1 = "GS1";
const GS2 = "GS2";
const GR2 = "GR2";
const NS3 = "NS3";
const GS1_COGS = "GS1_COGS";
const COGR = "COGR";
const NS3_COGS = "NS3_COGS";
const GS1_AUR = "GS1_AUR";
const GS1_AUC = "GS1_AUC";
const GS2_AUR = "GS2_AUR";
const GR2_AUR = "GR2_AUR";
const GR2_AUC = "GR2_AUC";
const NS3_AUR = "NS3_AUR";
const NS3_AUC = "NS3_AUC";
const GS2_MV = "GS2_MARGIN_VALUE";
const GS2_MP = "GS2_MARGIN_PERCENTAGE";
const NS3_MV = "NS3_MARGIN_VALUE";
const NS3_MP = "NS3_MARGIN_PERCENTAGE";
const GS_DC = "GS_DISCOUNT_COST";
const GS_DP = "GS_DISCOUNT_PERCENTAGE";
const RUP = "RETURN_UNIT_PERCENTAGE";
const RVP = "RETURN_VALUE_PERCENTAGE";
const RCP = "RETURN_COST_PERCENTAGE";

// Intake
const INTAKE_UNITS_TOTAL = "INTAKE_UNITS_TOTAL";
const INTAKE_UNITS_MAIN = "INTAKE_UNITS_MAIN";
const INTAKE_UNITS_REPLENISHMENT = "INTAKE_UNITS_REPLENISHMENT";
const INTAKE_UNITS_INSEASON = "INTAKE_UNITS_INSEASON";
const INTAKE_UNITS_CLEARANCE = "INTAKE_UNITS_CLEARANCE";
const INTAKE_AT_COST_TOTAL = "INTAKE_AT_COST_TOTAL";
const INTAKE_AT_COST_MAIN = "INTAKE_AT_COST_MAIN";
const INTAKE_AT_COST_REPLENISHMENT = "INTAKE_AT_COST_REPLENISHMENT";
const INTAKE_AT_COST_INSEASON = "INTAKE_AT_COST_INSEASON";
const INTAKE_AT_COST_CLEARANCE = "INTAKE_AT_COST_CLEARANCE";
const INTAKE_AUC_TOTAL = "INTAKE_AUC_TOTAL";
const INTAKE_AUC_MAIN = "INTAKE_AUC_MAIN";
const INTAKE_AUC_REPLENISHMENT = "INTAKE_AUC_REPLENISHMENT";
const INTAKE_AUC_INSEASON = "INTAKE_AUC_INSEASON";
const INTAKE_AUC_CLEARANCE = "INTAKE_AUC_CLEARANCE";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Division that returns 0 when denominator is 0.
 *  When `preserve` is provided and denominator is 0 but numerator is non-zero,
 *  returns the preserve value instead (prevents stuck-in-zero). */
function sd(n: number, d: number, preserve?: number): number {
  if (n === 0) return 0;
  if (d === 0) return preserve ?? 0;
  return n / d;
}

/** When AUR is 0, preserve existing value instead of returning 0. */
function preserveWhenZeroAur(
  aur: number,
  product: number,
  preserve: number
): number {
  return aur === 0 ? preserve : product;
}

function v(values: MetricValues, key: string): number {
  return values[key] ?? 0;
}

// ---------------------------------------------------------------------------
// Individual KPI calculation functions (trigger-aware)
// ---------------------------------------------------------------------------

type CalcFn = (values: MetricValues, trigger?: string) => number;

function calcGisI(values: MetricValues, trigger?: string): number {
  if (trigger === NS3)
    return sd(
      v(values, NS3) + v(values, GR2),
      v(values, GS2_AUR),
      v(values, GIS1)
    );
  if (trigger === NIS3) return v(values, GIR) + v(values, NIS3);
  if (trigger === NS3_COGS)
    return v(values, GIR) + sd(v(values, NS3_COGS), v(values, NS3_AUC));
  if (trigger === GS1_COGS)
    return sd(v(values, GS1_COGS), v(values, GS1_AUC), v(values, GIS1));
  if (trigger === GS2)
    return sd(v(values, GS2), v(values, GS2_AUR), v(values, GIS1));
  return sd(v(values, GS1), v(values, GS1_AUR), v(values, GIS1));
}

function calcGirII(values: MetricValues, trigger?: string): number {
  if (trigger === RUP) return v(values, RUP) * v(values, GIS1);
  // An RVP edit back-solves GR2, so units re-derive from GR2 either way.
  if (trigger === GR2 || trigger === RVP)
    return sd(v(values, GR2), v(values, GR2_AUR), v(values, GIR));
  if (trigger === COGR)
    return sd(v(values, COGR), v(values, GR2_AUC), v(values, GIR));
  return v(values, NIS3) - v(values, GIS1);
}

function calcNisIII(_values: MetricValues): number {
  return v(_values, GIS1) - v(_values, GIR);
}

function calcGsI(values: MetricValues): number {
  return preserveWhenZeroAur(
    v(values, GS1_AUR),
    v(values, GIS1) * v(values, GS1_AUR),
    v(values, GS1)
  );
}

function calcGsII(values: MetricValues): number {
  return preserveWhenZeroAur(
    v(values, GS2_AUR),
    v(values, GIS1) * v(values, GS2_AUR),
    v(values, GS2)
  );
}

function calcGrII(values: MetricValues, trigger?: string): number {
  if (trigger === NS3_AUR) return v(values, GS2) - v(values, NS3);
  if (trigger === RVP) return v(values, RVP) * v(values, GS2);
  return preserveWhenZeroAur(
    v(values, GR2_AUR),
    v(values, GIR) * v(values, GR2_AUR),
    v(values, GR2)
  );
}

function calcNsIII(values: MetricValues, trigger?: string): number {
  if (trigger === NS3_AUR) return v(values, NIS3) * v(values, NS3_AUR);
  return v(values, GS2) - v(values, GR2);
}

function calcGsCogs(values: MetricValues, trigger?: string): number {
  if (trigger === GS2_MP || trigger === GS2_MV)
    return v(values, GS2) - v(values, GS2_MV);
  // A net-margin entry fixes the net cost; the returns block is held, so the
  // cost of sales is what absorbs it.
  if (trigger === NS3_MP || trigger === NS3_MV)
    return v(values, NS3_COGS) + v(values, COGR);
  return v(values, GIS1) * v(values, GS1_AUC);
}

function calcCogr(values: MetricValues, trigger?: string): number {
  if (trigger === NS3_AUC) return v(values, GS1_COGS) - v(values, NS3_COGS);
  if (trigger === RCP) return v(values, RCP) * v(values, GS1_COGS);
  return v(values, GIR) * v(values, GR2_AUC);
}

function calcNsIIICogs(values: MetricValues, trigger?: string): number {
  if (trigger === NS3_MP || trigger === NS3_MV)
    return v(values, NS3) - v(values, NS3_MV);
  const nis3 = v(values, NIS3);
  const ns3Auc = v(values, NS3_AUC);
  // Fallback when NIS3 == 0 or NS3_AUC == 0 (stuck-in-zero) or NS3_AUC is
  // NaN (mirrors the pd.isna branch in calculate_ns_iii_cogs).
  if (nis3 === 0 || ns3Auc === 0 || Number.isNaN(ns3Auc)) {
    return v(values, GS1_COGS) - v(values, COGR);
  }
  return nis3 * ns3Auc;
}

function calcGsIAur(values: MetricValues): number {
  return sd(v(values, GS1), v(values, GIS1));
}

function calcGsIAuc(values: MetricValues): number {
  return sd(v(values, GS1_COGS), v(values, GIS1));
}

function calcGsIIAur(values: MetricValues): number {
  return sd(v(values, GS2), v(values, GIS1));
}

function calcGrIIAur(values: MetricValues): number {
  return sd(v(values, GR2), v(values, GIR));
}

function calcGrIIAuc(values: MetricValues): number {
  return sd(v(values, COGR), v(values, GIR));
}

function calcNsIIIAur(values: MetricValues): number {
  return sd(v(values, NS3), v(values, NIS3));
}

function calcNsIIIAuc(values: MetricValues): number {
  const nis3 = v(values, NIS3);
  // NaN (rendered blank) when underivable, matching calculate_ns_iii_auc:
  // the Python engine only fills (GS1_COGS - COGR) / NIS3 where NIS3 != 0.
  if (nis3 === 0) return Number.NaN;
  return (v(values, GS1_COGS) - v(values, COGR)) / nis3;
}

function calcGsIIMv(values: MetricValues, trigger?: string): number {
  if (trigger === GS2_MP) return v(values, GS2_MP) * v(values, GS2);
  return v(values, GS2) - v(values, GS1_COGS);
}

function calcGsIIMp(values: MetricValues): number {
  return sd(v(values, GS2_MV), v(values, GS2));
}

function calcNsIIIMv(values: MetricValues, trigger?: string): number {
  if (trigger === NS3_MP) return v(values, NS3_MP) * v(values, NS3);
  return v(values, NS3) - v(values, NS3_COGS);
}

function calcNsIIIMp(values: MetricValues): number {
  return sd(v(values, NS3_MV), v(values, NS3));
}

function calcGsDiscount(values: MetricValues): number {
  return v(values, GS1) - v(values, GS2);
}

function calcGsDiscountPct(values: MetricValues): number {
  return 1 - sd(v(values, GS2), v(values, GS1));
}

function calcRup(values: MetricValues): number {
  return sd(v(values, GIR), v(values, GIS1));
}

function calcRvp(values: MetricValues): number {
  return sd(v(values, GR2), v(values, GS2));
}

function calcRcp(values: MetricValues): number {
  return sd(v(values, COGR), v(values, GS1_COGS));
}

function calcIntakeUnitsTotal(values: MetricValues): number {
  return (
    v(values, INTAKE_UNITS_MAIN) +
    v(values, INTAKE_UNITS_REPLENISHMENT) +
    v(values, INTAKE_UNITS_INSEASON) -
    v(values, INTAKE_UNITS_CLEARANCE)
  );
}

function calcIntakeAtCostTotal(values: MetricValues): number {
  return (
    v(values, INTAKE_AT_COST_MAIN) +
    v(values, INTAKE_AT_COST_REPLENISHMENT) +
    v(values, INTAKE_AT_COST_INSEASON) -
    v(values, INTAKE_AT_COST_CLEARANCE)
  );
}

function calcIntakeAucTotal(values: MetricValues): number {
  return sd(v(values, INTAKE_AT_COST_TOTAL), v(values, INTAKE_UNITS_TOTAL));
}

function calcIntakeAucMain(values: MetricValues): number {
  return sd(v(values, INTAKE_AT_COST_MAIN), v(values, INTAKE_UNITS_MAIN));
}

function calcIntakeAucReplenishment(values: MetricValues): number {
  return sd(
    v(values, INTAKE_AT_COST_REPLENISHMENT),
    v(values, INTAKE_UNITS_REPLENISHMENT)
  );
}

function calcIntakeAucInseason(values: MetricValues): number {
  return sd(
    v(values, INTAKE_AT_COST_INSEASON),
    v(values, INTAKE_UNITS_INSEASON)
  );
}

function calcIntakeAucClearance(values: MetricValues): number {
  return sd(
    v(values, INTAKE_AT_COST_CLEARANCE),
    v(values, INTAKE_UNITS_CLEARANCE)
  );
}

// ---------------------------------------------------------------------------
// KPI_CALCULATIONS: metric → calculation function
// ---------------------------------------------------------------------------

const KPI_CALCULATIONS: Record<string, CalcFn> = {
  [GIS1]: calcGisI,
  [GIR]: calcGirII,
  [NIS3]: calcNisIII,
  [GS1]: calcGsI,
  [GS2]: calcGsII,
  [GR2]: calcGrII,
  [NS3]: calcNsIII,
  [GS1_COGS]: calcGsCogs,
  [COGR]: calcCogr,
  [NS3_COGS]: calcNsIIICogs,
  [GS1_AUR]: calcGsIAur,
  [GS1_AUC]: calcGsIAuc,
  [GS2_AUR]: calcGsIIAur,
  [GR2_AUR]: calcGrIIAur,
  [GR2_AUC]: calcGrIIAuc,
  [NS3_AUR]: calcNsIIIAur,
  [NS3_AUC]: calcNsIIIAuc,
  [GS2_MV]: calcGsIIMv,
  [GS2_MP]: calcGsIIMp,
  [NS3_MV]: calcNsIIIMv,
  [NS3_MP]: calcNsIIIMp,
  [GS_DC]: calcGsDiscount,
  [GS_DP]: calcGsDiscountPct,
  [RUP]: calcRup,
  [RVP]: calcRvp,
  [RCP]: calcRcp,
  [INTAKE_UNITS_TOTAL]: calcIntakeUnitsTotal,
  [INTAKE_AT_COST_TOTAL]: calcIntakeAtCostTotal,
  [INTAKE_AUC_TOTAL]: calcIntakeAucTotal,
  [INTAKE_AUC_MAIN]: calcIntakeAucMain,
  [INTAKE_AUC_REPLENISHMENT]: calcIntakeAucReplenishment,
  [INTAKE_AUC_INSEASON]: calcIntakeAucInseason,
  [INTAKE_AUC_CLEARANCE]: calcIntakeAucClearance
};

// ---------------------------------------------------------------------------
// SIMPLE_KPI_PIPELINES: trigger → dependents to recalculate
// ---------------------------------------------------------------------------

// Mirrors kpi_logic.py's SIMPLE_KPI_PIPELINES including the 2026-06 phase-X
// fixes (GS_DC/GS_DP, NS3_MV/NS3_MP, return percentages and GS2 margins as
// dependents; GS1_COGS before NS3_COGS in the NS3 pipeline). Dependents are
// ordered after their inputs; parity with the Python engine is pinned by
// kpiCalculations.parity.test.ts against calc-vectors/kpi-cascade.json.
const SIMPLE_KPI_PIPELINES: Record<string, string[]> = {
  [GIS1]: [
    NIS3,
    GS1,
    GS2,
    GS1_COGS,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [GIR]: [
    NIS3,
    GR2,
    COGR,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [NIS3]: [
    GIS1,
    GS1,
    GS2,
    GS1_COGS,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [GS1]: [
    GIS1,
    NIS3,
    GS1_COGS,
    GS2,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [GS2]: [
    GIS1,
    NIS3,
    GS1,
    GS1_COGS,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [GR2]: [
    GIR,
    NIS3,
    COGR,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [NS3]: [
    GIS1,
    GS1,
    GS2,
    NIS3,
    // GS1_COGS must precede NS3_COGS: calcNsIIICogs falls back to
    // GS1_COGS - COGR, so a stale GS1_COGS here contradicts re-derivation.
    GS1_COGS,
    NS3_COGS,
    NS3_AUR,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3_MV,
    NS3_MP,
    RUP,
    RVP,
    RCP
  ],
  [GS1_COGS]: [
    GIS1,
    NIS3,
    GS2,
    GS1,
    GS1_COGS,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [COGR]: [
    GIR,
    NIS3,
    GR2,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [NS3_COGS]: [
    GIS1,
    GS1,
    GS1_COGS,
    GS2,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NIS3,
    NS3,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RVP,
    RCP
  ],
  [GS1_AUR]: [GS1, GS_DC, GS_DP],
  [GS2_AUR]: [
    GS2,
    GS2_MV,
    GS2_MP,
    GS_DC,
    GS_DP,
    NS3,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RVP
  ],
  [GR2_AUR]: [GR2, NS3, NS3_MV, NS3_MP, NS3_AUR, RVP],
  [NS3_AUR]: [NS3, NS3_MV, NS3_MP, GR2, GR2_AUR, RVP],
  [GS1_AUC]: [GS1_COGS, GS2_MV, GS2_MP, NS3_AUC, NS3_COGS, NS3_MV, NS3_MP, RCP],
  // Inverse of the GS1_AUC edit: hold GS2 and GIS1, back-solve the cost, and
  // let GS1_AUC absorb it.
  [GS2_MP]: [GS2_MV, GS1_COGS, GS1_AUC, NS3_AUC, NS3_COGS, NS3_MV, NS3_MP, RCP],
  // The margin typed in currency instead of as a share — same back-solve, so
  // the pair stays reachable from either end.
  [GS2_MV]: [GS2_MP, GS1_COGS, GS1_AUC, NS3_AUC, NS3_COGS, NS3_MV, NS3_MP, RCP],
  // Net-side twin of the GS2_MP edit: hold NS3 (and the returns block), so the
  // net cost carries the entry up into GS1_COGS and GS1_AUC absorbs it.
  [NS3_MP]: [NS3_MV, NS3_COGS, GS1_COGS, GS1_AUC, NS3_AUC, GS2_MV, GS2_MP, RCP],
  [NS3_MV]: [NS3_MP, NS3_COGS, GS1_COGS, GS1_AUC, NS3_AUC, GS2_MV, GS2_MP, RCP],
  [GR2_AUC]: [COGR, NS3_AUC, NS3_COGS, NS3_MV, NS3_MP, RCP],
  [NS3_AUC]: [NS3_COGS, NS3_MV, NS3_MP, COGR, RCP, GR2_AUC],
  // Editing RVP holds GS2 constant and back-solves GR2 = RVP * GS2, then
  // runs GR2's downstream cascade.
  [RVP]: [
    GR2,
    GIR,
    NIS3,
    COGR,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RUP,
    RCP
  ],
  // RUP is RVP's unit-side mirror: hold GIS1, back-solve GIR = RUP * GIS1,
  // then run GIR's downstream cascade.
  [RUP]: [
    GIR,
    NIS3,
    GR2,
    COGR,
    NS3,
    NS3_COGS,
    NS3_MV,
    NS3_MP,
    NS3_AUR,
    RVP,
    RCP
  ],
  // Inverse of the GR2_AUC edit: hold GS1_COGS, back-solve COGR = RCP *
  // GS1_COGS, and let GR2_AUC absorb it — units and every value stay put.
  [RCP]: [COGR, GR2_AUC, NS3_AUC, NS3_COGS, NS3_MV, NS3_MP],
  [INTAKE_UNITS_REPLENISHMENT]: [
    INTAKE_UNITS_TOTAL,
    INTAKE_AUC_REPLENISHMENT,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_UNITS_MAIN]: [INTAKE_UNITS_TOTAL, INTAKE_AUC_MAIN, INTAKE_AUC_TOTAL],
  [INTAKE_UNITS_INSEASON]: [
    INTAKE_UNITS_TOTAL,
    INTAKE_AUC_INSEASON,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_UNITS_CLEARANCE]: [
    INTAKE_UNITS_TOTAL,
    INTAKE_AUC_CLEARANCE,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_AT_COST_REPLENISHMENT]: [
    INTAKE_AT_COST_TOTAL,
    INTAKE_AUC_REPLENISHMENT,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_AT_COST_MAIN]: [
    INTAKE_AT_COST_TOTAL,
    INTAKE_AUC_MAIN,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_AT_COST_INSEASON]: [
    INTAKE_AT_COST_TOTAL,
    INTAKE_AUC_INSEASON,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_AT_COST_CLEARANCE]: [
    INTAKE_AT_COST_TOTAL,
    INTAKE_AUC_CLEARANCE,
    INTAKE_AUC_TOTAL
  ],
  [INTAKE_AT_COST_TOTAL]: [INTAKE_AUC_TOTAL],
  [INTAKE_UNITS_TOTAL]: [INTAKE_AUC_TOTAL]
};

// ---------------------------------------------------------------------------
// Top-level API
// ---------------------------------------------------------------------------

/**
 * Applies a KPI change to a set of metric values and recalculates all
 * dependent metrics.
 *
 * @param values - Current metric values for a single (table, row, agg) cell.
 *   Modified in place and also returned.
 * @param trigger - The metric that was edited (e.g. "GS1_AUR").
 * @param newValue - The new value for the trigger metric.
 * @returns The mutated values with all dependents recalculated.
 */
export function applyKpiChange(
  values: MetricValues,
  trigger: string,
  newValue: number
): MetricValues {
  // 1. Apply the edit.
  values[trigger] = newValue;

  // 2. Run the pipeline for this trigger.
  const pipeline = SIMPLE_KPI_PIPELINES[trigger];
  if (pipeline) {
    for (const dependent of pipeline) {
      const calcFn = KPI_CALCULATIONS[dependent];
      if (calcFn) {
        values[dependent] = calcFn(values, trigger);
      }
    }
  }

  return values;
}

/**
 * Checks whether a given metric has a KPI pipeline (i.e. whether editing it
 * will trigger reverse derivation of dependent metrics).
 */
export function hasKpiPipeline(metric: string): boolean {
  return metric in SIMPLE_KPI_PIPELINES;
}
